import { useCallback, useEffect, useState } from 'react';
import { getAppList, getCommission, isConnected } from '../services/deriv';
import { getCommissionBreakdown, getCommissionRange } from '../services/commission';

/**
 * Loads per-app commission stats (today / this month / last month, contracts,
 * clients, share of total) once and shares it between the Overview "Top
 * Performing Apps" section and the full All Apps tab, so switching tabs
 * doesn't re-fetch the same breakdowns.
 */
export function useAppStats() {
  const [apps, setApps] = useState([]);
  const [appStats, setAppStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({
    totalApps: 0,
    activeApps: 0,
    totalRevenue: 0,
    totalClients: 0,
    totalContracts: 0,
  });

  const load = useCallback(async () => {
    if (!isConnected()) return;
    setLoading(true);

    try {
      const appListRes = await getAppList();
      const appList = appListRes.app_list || [];

      const todayRange = getCommissionRange('today');
      const thisMonthRange = getCommissionRange('thisMonth');
      const lastMonthRange = getCommissionRange('lastMonth');

      const todayRes = await getCommission(todayRange.from, todayRange.to);
      const todayBreakdown = getCommissionBreakdown(todayRes);

      const thisMonthRes = await getCommission(thisMonthRange.from, thisMonthRange.to);
      const thisMonthBreakdown = getCommissionBreakdown(thisMonthRes);

      const lastMonthRes = await getCommission(lastMonthRange.from, lastMonthRange.to);
      const lastMonthBreakdown = getCommissionBreakdown(lastMonthRes);

      const stats = {};
      const monthTotals = thisMonthRes?.app_markup_statistics || {};
      const totalRev = monthTotals.total_app_markup_usd || 0;
      const clientTotal = monthTotals.total_client_count || 0;
      const contractTotal = monthTotals.total_contract_count || monthTotals.total_transactions_count || 0;

      thisMonthBreakdown.forEach((item) => {
        const appId = item.app_id;
        stats[appId] = {
          today: 0,
          thisMonth: item.app_markup_usd || 0,
          lastMonth: 0,
          transactions: item.transactions_count || 0,
          volume: item.volume_usd || 0,
          payout: item.payout_usd || 0,
          clients: item.client_count || 0,
          sharePercent: 0,
        };
      });

      lastMonthBreakdown.forEach((item) => {
        const appId = item.app_id;
        const commission = item.app_markup_usd || 0;

        if (stats[appId]) {
          stats[appId].lastMonth = commission;
          stats[appId].clients = Math.max(stats[appId].clients || 0, item.client_count || 0);
        } else {
          stats[appId] = {
            today: 0,
            thisMonth: 0,
            lastMonth: commission,
            transactions: item.transactions_count || 0,
            volume: 0,
            payout: 0,
            clients: item.client_count || 0,
            sharePercent: 0,
          };
        }
      });

      todayBreakdown.forEach((item) => {
        const appId = item.app_id;
        const commission = item.app_markup_usd || 0;

        if (stats[appId]) {
          stats[appId].today = commission;
          stats[appId].clients = Math.max(stats[appId].clients || 0, item.client_count || 0);
        } else {
          stats[appId] = {
            today: commission,
            thisMonth: 0,
            lastMonth: 0,
            transactions: item.transactions_count || 0,
            volume: item.volume_usd || 0,
            payout: item.payout_usd || 0,
            clients: item.client_count || 0,
            sharePercent: 0,
          };
        }
      });

      // Normalize all app_ids to strings before dedup — Object.keys gives strings
      // but appList entries may carry numeric ids, causing Map to see them as different keys
      const appsFromStats = Object.keys(stats).map((appId) => ({ app_id: appId }));
      const availableApps = Array.from(
        [...appsFromStats, ...appList]
          .map((app) => ({ ...app, app_id: String(app.app_id) }))
          .reduce((map, app) => map.set(app.app_id, app), new Map())
          .values()
      );

      const appsWithData = availableApps.filter((app) => stats[app.app_id]);

      Object.keys(stats).forEach((appId) => {
        if (totalRev > 0) {
          stats[appId].sharePercent = ((stats[appId].thisMonth / totalRev) * 100).toFixed(2);
        }
      });

      const activeCount = appsWithData.filter((app) => stats[app.app_id]?.thisMonth > 0).length;

      setApps(appsWithData);
      setAppStats(stats);
      setTotals({
        totalApps: appsWithData.length,
        activeApps: activeCount,
        totalRevenue: totalRev,
        totalClients: clientTotal,
        totalContracts: contractTotal,
      });
    } catch (error) {
      console.error('Error loading app stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { apps, appStats, loading, totals, reload: load };
}
